import { effect, inject, Injectable, signal } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { catchError, map, Observable, of, Subject, take, timeout } from 'rxjs';
import { APP_CONSTANTS } from 'src/app/app.constants';
import UID from 'src/app/app.utils';
import { DeviationSurveyService } from 'src/app/features/detail-view/deviation-survey/deviation-survey.service';
import {
  resetMalfunctionArray,
  resetViolationArray,
} from 'src/app/features/detail-view/well-status/well-status.constants';
import { environment } from 'src/environments/environment';
import { Well } from '../interfaces/well.interface';
import {
  MediationType,
  MotorStateDailyAccumulation,
  MotorStateSample,
  OperationalState,
  OperationalStateSample,
  SensorsData,
  SpanAggregateWellMotorState_RES,
} from '../interfaces/wellprotocol.interface';
import { WsMessage, WsService } from './ws.service';

@Injectable({ providedIn: 'root' })
export abstract class WellService {
  deviationSurveyService = inject(DeviationSurveyService);
  // #region Signals
  protected readonly _signals = {
    wells: signal<Well[]>([]),
    currentWell: signal<Well | null>(null),
    liveUsers: signal<number>(0),
    connectionStatus: signal<number | null>(null),
    syncCheck: signal<any | null>(null),
    motorState: signal<MotorStateSample | null>(null),
    operationalState: signal<OperationalStateSample | null>(null),
    stroke: signal<any | null>(null),
    strokeCards: signal<any | null>(null),
    motorStateDailyAccumulation: signal<MotorStateDailyAccumulation | null>(null),
    strokeDailyAccumulation: signal<any | null>(null),
    violationActiveAccumulation: signal<any | null>(null),
    sensorData: signal<any | null>(null),
    accumulatedRuntimeCycles: signal<any | null>(null),
    strokeHistoricalAccumulation: signal<any | null>(null),
  };

  readonly wells = this._signals.wells.asReadonly();
  readonly currentWell = this._signals.currentWell.asReadonly();
  readonly currentWellLiveUsers = this._signals.liveUsers.asReadonly();
  readonly currentWellConnectionStatus = this._signals.connectionStatus.asReadonly();
  readonly currentWellSyncCheck = this._signals.syncCheck.asReadonly();
  readonly currentWellMotorState = this._signals.motorState.asReadonly();
  readonly currentWellOperationalState = this._signals.operationalState.asReadonly();
  readonly currentWellStroke = this._signals.stroke.asReadonly();
  readonly currentWellStrokeCards = this._signals.strokeCards.asReadonly();
  readonly currentWellMotorStateDailyAccumulation =
    this._signals.motorStateDailyAccumulation.asReadonly();
  readonly currentWellStrokeDailyAccumulation = this._signals.strokeDailyAccumulation.asReadonly();
  readonly currentWellViolationActiveAccumulation =
    this._signals.violationActiveAccumulation.asReadonly();
  readonly currentWellSensorData = this._signals.sensorData.asReadonly();
  readonly currentWellAccumulatedRuntimeCycles =
    this._signals.accumulatedRuntimeCycles.asReadonly();
  readonly currentWellStrokeHistoricalAccumulation =
    this._signals.strokeHistoricalAccumulation.asReadonly();

  /** Why the last GET_WELL reply didn't yield a usable well, while WellServiceHMI keeps retrying:
   *  'unconfigured' for a successful reply with no purpose, as a freshly activated controller
   *  sends until it first syncs with the backend; 'failed' for an error reply. null otherwise, and
   *  always on the web build, which never sets it. Kept outside _signals, which the HMI release
   *  path resets to null. */
  protected readonly _wellLoadIssue = signal<'unconfigured' | 'failed' | null>(null);
  readonly wellLoadIssue = this._wellLoadIssue.asReadonly();

  // #endregion

  private readonly _position = signal<google.maps.LatLngLiteral>({
    ...APP_CONSTANTS.DEFAULT_MAP_POSITION,
  });
  /** Map center used by MapViewComponent — reactive so the map recenters even when
   *  navigating to a URL it's already on (e.g. repeated "View well on map" clicks). */
  readonly position = this._position.asReadonly();

  /** Updates the map center. Only the given fields are changed; the rest are preserved. */
  public setPosition(pos: Partial<google.maps.LatLngLiteral>): void {
    this._position.update((current) => ({ ...current, ...pos }));
  }

  // Feedback IDs for well-status accumulation requests — used to filter out POH responses.
  private _strokeAccumFeedbackId: number | null = null;
  private _runtimeCycleAccumFeedbackId: number | null = null;

  // Tracks how many callers have subscribed to each well event so that
  // unsubscribeFromWellEvent only sends the server-side UNSUBSCRIBE when the
  // last subscriber releases. This prevents the Angular tab-switch race where
  // the incoming component's ngOnInit subscribes before the outgoing component's
  // ngOnDestroy unsubscribes, causing the outgoing destroy to kill the incoming
  // component's live stream.
  private readonly _eventRefCounts = new Map<string, number>();

  ws = inject(WsService);
  toastr = inject(ToastrService);
  private readonly _saveWellResult$ = new Subject<{ success: boolean; id?: string }>();
  readonly saveResult$ = this._saveWellResult$.pipe(map((result) => result.success));
  private readonly _mediateResult$ = new Subject<boolean>();

  // Bumped on every SESSION_EVENTS.CONNECTION_ESTABLISHED push (including the very first
  // connect, matching the WellServiceKenLive convention of not bothering to guard against a
  // redundant first-run resubscribe). Empty string for HMI (which reloads the page on reconnect
  // instead — see WsService), so this never fires there. Read, not written, by
  // DetailViewComponent's setup effect so a mid-session reconnect re-arms the well-scoped
  // subscriptions the server has forgotten, rather than leaving them dead until the operator
  // happens to navigate away and back.
  private readonly _reconnectCount = signal(0);
  readonly reconnected = this._reconnectCount.asReadonly();

  // True while a GET_WELL is awaiting its reply. A WELL_MODIFICATION push that lands meanwhile is
  // parked instead of fetched, then compared with the reply's timePoint once it arrives — so the
  // two echoes the server sends when we (re)subscribe never cost a second fetch, whichever side
  // of the reply they land on, while any other modification still gets one. The latest push
  // wins the slot, since it describes the latest stored state.
  private _wellFetchInFlight = false;
  private _parkedModification: { id: string; timePoint: number | null } | null = null;

  // Persistent toasts tracking the well's actual operational state, rather than a fixed
  // timer — cleared as soon as the state moves on, however that happened (this user's click,
  // another operator, or an automatic transition).
  private startingToast: any | null = null;
  private stoppingToast: any | null = null;

  /** True if a well-scoped WS push carries a body and its itemId matches the currently viewed
   *  well. Error replies arrive with a null body and are rejected. The itemId check is skipped
   *  in HMI mode, whose messages carry no itemId. */
  private isForCurrentWell(body: WsMessage['body']): body is NonNullable<WsMessage['body']> {
    if (!body) return false;
    if (environment.isHMIMode) return true;
    const itemId = body['itemId'];
    return !!itemId && itemId === this._signals.currentWell()?.id;
  }

  constructor() {
    // #region WebSocket Subscriptions
    this.ws.subscribeWs(APP_CONSTANTS.WELL_EVENTS.GET_CONNECTION_STATUS, (msg) => {
      if (!msg.body) return;
      const { userId: wellId, connectionStatus } = msg.body;
      const currentWellId = this._signals.currentWell()?.id;
      if (wellId === currentWellId) {
        this._signals.connectionStatus.set(connectionStatus);
      }
      this.updateConnectionStatusInWellList(wellId, connectionStatus);
    });
    this.ws.subscribeWs(APP_CONSTANTS.WELL_EVENTS.GET_SYNC_CHECK, (msg) => {
      if (this.isForCurrentWell(msg.body)) {
        this._signals.syncCheck.set(msg.body['data']);
      }
    });
    this.ws.subscribeWs(APP_CONSTANTS.WELL_EVENTS.GET_MOTOR_STATE, (msg) => {
      if (this.isForCurrentWell(msg.body)) {
        this._signals.motorState.set(msg.body['data']);
      }
    });
    this.ws.subscribeWs(APP_CONSTANTS.WELL_EVENTS.GET_OPERATIONAL_STATE, (msg) => {
      if (this.isForCurrentWell(msg.body)) {
        this._signals.operationalState.set(msg.body['data']);
      }
    });
    this.ws.subscribeWs(APP_CONSTANTS.WELL_EVENTS.GET_STROKE_DATA, (msg) => {
      if (this.isForCurrentWell(msg.body)) {
        this._signals.stroke.set(this.processStroke(msg.body['data']));
      }
    });
    this.ws.subscribeWs(APP_CONSTANTS.WELL_EVENTS.GET_STROKE_CARDS, (msg) => {
      if (this.isForCurrentWell(msg.body)) {
        this._signals.strokeCards.set(this.processStrokeCard(msg.body['data']));
      }
    });
    this.ws.subscribeWs(APP_CONSTANTS.WELL_EVENTS.GET_MOTOR_STATE_DAILY_ACCUMULATION, (msg) => {
      if (this.isForCurrentWell(msg.body)) {
        this._signals.motorStateDailyAccumulation.set(msg.body['data']);
      }
    });
    this.ws.subscribeWs(APP_CONSTANTS.WELL_EVENTS.GET_STROKE_DAILY_ACCUMULATION, (msg) => {
      if (this.isForCurrentWell(msg.body)) {
        this._signals.strokeDailyAccumulation.set(msg.body['data']);
      }
    });
    this.ws.subscribeWs(APP_CONSTANTS.WELL_EVENTS.GET_VIOLATION_ACTIVE_ACCUMULATION, (msg) => {
      if (this.isForCurrentWell(msg.body)) {
        this._signals.violationActiveAccumulation.set(this.processViolationsData(msg.body));
      }
    });
    this.ws.subscribeWs(APP_CONSTANTS.WELL_EVENTS.GET_SENSOR_DATA, (msg) => {
      if (this.isForCurrentWell(msg.body)) {
        this._signals.sensorData.set(this.processSensorData(msg.body['data']));
      }
    });
    this.ws.subscribeWs(APP_CONSTANTS.RESPONSE_MESSAGES.GET_ACCUMULATED_RUNTIME_CYCLES, (msg) => {
      if (msg.body && msg.header['feedbackId'] === this._runtimeCycleAccumFeedbackId) {
        this._signals.accumulatedRuntimeCycles.set(this.processRuntimeCycles(msg.body['data']));
      }
    });
    this.ws.subscribeWs(APP_CONSTANTS.RESPONSE_MESSAGES.GET_ACCUMULATED_STROKE_DATA, (msg) => {
      if (msg.header['feedbackId'] === this._strokeAccumFeedbackId) {
        this._signals.strokeHistoricalAccumulation.set(msg.body?.['records']?.[0]?.data ?? null);
      }
    });
    this.ws.subscribeWs(APP_CONSTANTS.RESPONSE_MESSAGES.SET_WELL, (msg) => {
      const status = msg.header?.['status'];
      const summary = msg.header?.['summary'];
      const currentWellId = this._signals.currentWell()?.id ?? undefined;
      // UPDATE_SUCCESS_BUT_CONTROLLER_FAILED carries a non-4000 status, but the write already
      // landed in Mongo — the controller sync it's still waiting on happens independently and
      // doesn't need the form to remain "unsaved" in the meantime, so it counts as saved here
      // the same as a clean 4000.
      const saved =
        status === 4000 ||
        summary === APP_CONSTANTS.RESPONSE_SUMMARIES.UPDATE_SUCCESS_BUT_CONTROLLER_FAILED;
      this._saveWellResult$.next({ success: saved, id: msg.body?.['id'] });
      if (currentWellId) this.requestWell(String(currentWellId));
    });
    // Nothing previously subscribed to Mediate's response, so a rejected or dropped
    // startWell()/stopWell() surfaced no feedback at all — this both feeds watchMediateWell()'s
    // timeout fallback below and, as a side effect of subscribeWs's shared returnResponse
    // plumbing, gives reboot/calibrate/reset-malfunctions/reset-violations a rejection toast
    // they never had either, since they all share this same response message.
    this.ws.subscribeWs(APP_CONSTANTS.RESPONSE_MESSAGES.SET_MEDIATE_WELL, (msg) => {
      this._mediateResult$.next(msg.header?.['status'] === 4000);
    });
    this.ws.subscribeWs(APP_CONSTANTS.SESSION_EVENTS.CONNECTION_ESTABLISHED, () => {
      this._wellFetchInFlight = false;
      this._parkedModification = null;
      this._reconnectCount.update((n) => n + 1);
    });
    // Re-fetch the current well when a modification other than our copy is pushed — another
    // client's save, or the controller rewriting fields after ours (e.g. channel ranges it
    // derives itself). Echoes of the copy we already hold carry its own timePoint and are skipped.
    this.ws.subscribeWs(APP_CONSTANTS.WELL_EVENTS.WELL_MODIFICATION, (msg) => {
      const modification = msg.body?.['data']?.['wellModification'];
      if (modification?.['applied'] !== 1 || !this.isForCurrentWell(msg.body)) return;
      const wellId = this._signals.currentWell()?.id;
      if (!wellId) return;
      const id = String(wellId);
      const timePoint: number | null = modification['timePoint'] ?? null;
      if (this._wellFetchInFlight) {
        this._parkedModification = { id, timePoint };
        return;
      }
      if (!this.matchesCurrentWell(timePoint)) this.requestWell(id);
    });
    // #endregion

    // Shows "Starting the Well..."/"Stopping the Well..." while the well's operational state
    // is actually Starting / anywhere in the Stopping range, and dismisses them as soon as it
    // moves past that — instead of a fixed timer that may outlast or cut off the real transition.
    effect(() => {
      const state = this._signals.operationalState()?.state;
      this.syncTransitionToast(
        'startingToast',
        state === OperationalState.Starting,
        'Starting the Well...',
      );
      this.syncTransitionToast('stoppingToast', this.isWellStateStopping(), 'Stopping the Well...');
    });
  }

  /** Sends GET_WELL for the given well id and marks a fetch as in flight. */
  protected requestWell(id: string): void {
    this._wellFetchInFlight = true;
    this.ws.sendWsRequest(
      { name: APP_CONSTANTS.REQUEST_MESSAGES.GET_WELL },
      { filter: { id: { $eq: id } } },
    );
  }

  /** Subclasses call this after handling every GET_WELL reply, error replies included, once
   *  currentWell holds whatever the reply yielded. Fetches again only when a modification parked
   *  during the request is not the well that came back. */
  protected onWellReply(): void {
    this._wellFetchInFlight = false;
    const parked = this._parkedModification;
    this._parkedModification = null;
    const id = this._signals.currentWell()?.id;
    if (parked && id && parked.id === String(id) && !this.matchesCurrentWell(parked.timePoint)) {
      this.requestWell(parked.id);
    }
  }

  /** True when a modification carries exactly the held well's timePoint, i.e. it echoes the copy
   *  we already have. Equality, not ordering: on the web build the held well may carry WELLBED's
   *  stamp while the controller restamps its copy with its own clock (ModifyWell is applied with
   *  trustTimePoint = false), so the controller's newer state can compare as older. A missing
   *  timePoint on either side never matches. */
  private matchesCurrentWell(timePoint: number | null): boolean {
    const current = this._signals.currentWell()?.timePoint;
    return timePoint != null && current != null && timePoint === current;
  }

  /** Opens or clears one of the persistent state-driven toasts (see the effect above). */
  private syncTransitionToast(
    key: 'startingToast' | 'stoppingToast',
    isActive: boolean,
    message: string,
  ): void {
    if (isActive && !this[key]) {
      this[key] = this.toastr.info(message, '', {
        disableTimeOut: true,
        tapToDismiss: false,
        toastClass: 'ngx-toastr w-100 w-md-50 w-lg-25 toast-no-pointer',
      });
    } else if (!isActive && this[key]) {
      this.toastr.clear(this[key].toastId);
      this[key] = null;
    }
  }

  // #region Public API

  abstract subscribeToWellList(): void;
  abstract setCurrentWellById(id?: string): void;
  abstract saveWellInfo(sendData: any): Observable<boolean>;
  abstract addWell(data: any): Observable<{ success: boolean; id?: string }>;

  /** Waits for the next SET_WELL response and returns an observable that emits the save result. */
  protected watchSaveWell(): Observable<boolean> {
    return this.watchSaveWellResult().pipe(map((result) => result.success));
  }

  /** Same as watchSaveWell(), but also surfaces the id SET_WELL's response carries — the newly
   *  generated id on an insert (UpsertWell::RES), used by addWell() to redirect straight to the
   *  new well instead of the well list. */
  protected watchSaveWellResult(): Observable<{ success: boolean; id?: string }> {
    return this._saveWellResult$.pipe(
      take(1),
      timeout(8000),
      catchError(() => {
        this.toastr.error('No response from server. Please try again.', '', {
          disableTimeOut: true,
          tapToDismiss: true,
        });
        return of({ success: false });
      }),
    );
  }

  /** Waits for the next Mediate response, the same fallback watchSaveWell() gives SET_WELL —
   *  a rejected or dropped mediation otherwise raised no feedback at all. */
  private watchMediateWell(): Observable<boolean> {
    return this._mediateResult$.pipe(
      take(1),
      timeout(8000),
      catchError(() => {
        this.toastr.error('No response from server. Please try again.', '', {
          disableTimeOut: true,
          tapToDismiss: true,
        });
        return of(false);
      }),
    );
  }

  /** Decrements the ref-count for the event and only sends the server-side UNSUBSCRIBE
   *  when the last subscriber releases. Extra calls (count already at 0) send the UNSUBSCRIBE
   *  once and are otherwise harmless. */
  public unsubscribeFromWellEvent(eventName: string): void {
    const count = this._eventRefCounts.get(eventName) ?? 0;
    if (count <= 1) {
      this._eventRefCounts.delete(eventName);
      this.ws.sendWsRequest(
        { name: APP_CONSTANTS.REQUEST_MESSAGES.UNSUBSCRIBE_WELL },
        { itemEventName: eventName },
      );
    } else {
      this._eventRefCounts.set(eventName, count - 1);
    }
  }

  /** Increments the ref-count for the event, then unsubscribes any stale server-side listener
   *  and re-subscribes with the given well id.
   *  Pass null as id to subscribe at the global/list level (e.g. connection status for the map view). */
  public subscribeToWellEvent(id: string | null, eventName: string): void {
    this._eventRefCounts.set(eventName, (this._eventRefCounts.get(eventName) ?? 0) + 1);
    this.ws.sendWsRequest(
      { name: APP_CONSTANTS.REQUEST_MESSAGES.UNSUBSCRIBE_WELL },
      { itemEventName: eventName },
    );
    this.ws.sendWsRequest(
      { name: APP_CONSTANTS.REQUEST_MESSAGES.SUBSCRIBE_WELL },
      { itemId: id, itemEventName: eventName },
    );
  }

  /** Re-issues the wire-level SUBSCRIBE_WELL for an event without touching its ref count.
   *  A reconnect resets the server's own subscription bookkeeping even though this service's
   *  ref count — and every component still holding a subscription — never went away, so callers
   *  use this instead of subscribeToWellEvent/unsubscribeFromWellEvent to avoid double-counting
   *  a logical subscriber that never actually released. */
  public resubscribeWellEvent(id: string | null, eventName: string): void {
    this.ws.sendWsRequest(
      { name: APP_CONSTANTS.REQUEST_MESSAGES.SUBSCRIBE_WELL },
      { itemId: id, itemEventName: eventName },
    );
  }

  /** Requests aggregated runtime-cycle data for the given well and time window.
   *  Results are emitted via GET_ACCUMULATED_RUNTIME_CYCLES and stored in accumulatedRuntimeCycles signal.
   *  @param descriptors — field descriptors controlling which cycle metrics are aggregated. */
  public subscribeToAccumulatedWellCycles(
    id: string,
    start: number,
    end: number,
    period: number,
    offset: number,
    descriptors: any[] = [],
  ): void {
    const uid = UID();
    this._runtimeCycleAccumFeedbackId = uid;
    this.ws.sendWsRequest(
      { name: APP_CONSTANTS.REQUEST_MESSAGES.GET_ACCUMULATED_RUNTIME_CYCLES, feedbackId: uid },
      {
        filter: { timePoint: { $gte: start, $lt: end }, itemId: { $eq: id } },
        span: { timePoint: { period, offset } },
        aggMask: {
          timePoint: { max: 1 },
          data: { timePoint: { max: 1 } },
        },
        descriptors,
      },
    );
  }

  /** Requests aggregated stroke data (production totals, load min/max, avg duration) for the
   *  given well and time window. Results are stored in strokeHistoricalAccumulation signal. */
  public subscribeToAccumulatedStrokeData(
    id: string,
    start: number,
    end: number,
    period: number,
    offset: number,
  ): void {
    const uid = UID();
    this._strokeAccumFeedbackId = uid;
    this.ws.sendWsRequest(
      { name: APP_CONSTANTS.REQUEST_MESSAGES.GET_ACCUMULATED_STROKE_DATA, feedbackId: uid },
      {
        filter: { timePoint: { $gte: start, $lt: end }, itemId: { $eq: id } },
        span: { timePoint: { period, offset } },
        aggMask: {
          timePoint: { max: 1 },
          data: {
            timePoint: { max: 1 },
            duration: { avg: 1 },
            oilProduced: { sum: 1 },
            gasProduced: { sum: 1 },
            waterProduced: { sum: 1 },
            liquidProduced: { sum: 1 },
            sfMinLoad: { min: 1 },
            sfMaxLoad: { max: 1 },
          },
        },
      },
    );
  }

  /** Sends SpanAggregateWellStrokeData with a dynamic aggMask built from the given field
   *  descriptors. Returns the feedbackId so the caller can match the response. */
  public requestPohStrokeData(
    id: string,
    start: number,
    end: number,
    period: number,
    offset: number,
    fields: Record<string, { parentPath: string; childPath: string }>,
  ): number {
    const uid = UID();
    const aggMaskData: Record<string, any> = { timePoint: { max: 1 } };
    for (const { parentPath, childPath } of Object.values(fields)) {
      if (!aggMaskData[parentPath]) aggMaskData[parentPath] = {};
      aggMaskData[parentPath][childPath] = 1;
    }
    this.ws.sendWsRequest(
      { name: APP_CONSTANTS.REQUEST_MESSAGES.GET_ACCUMULATED_STROKE_DATA, feedbackId: uid },
      {
        filter: { timePoint: { $gte: start, $lt: end }, itemId: { $eq: id } },
        span: { timePoint: { period, offset } },
        aggMask: { timePoint: { max: 1 }, data: aggMaskData },
      },
    );
    return uid;
  }

  /** Sends SpanAggregateWellRuntimeCycles with per-field descriptors.
   *  Returns the feedbackId so the caller can match the response. */
  public requestPohRuntimeCycles(
    id: string,
    start: number,
    end: number,
    period: number,
    offset: number,
    descriptors: any[],
  ): number {
    const uid = UID();
    this.ws.sendWsRequest(
      { name: APP_CONSTANTS.REQUEST_MESSAGES.GET_ACCUMULATED_RUNTIME_CYCLES, feedbackId: uid },
      {
        filter: { timePoint: { $gte: start, $lt: end }, itemId: { $eq: id } },
        span: { timePoint: { period, offset } },
        aggMask: { timePoint: { max: 1 }, data: { timePoint: { max: 1 } } },
        descriptors,
      },
    );
    return uid;
  }

  /** Sends SpanAggregateWellDowntimes with per-filter stateFilters.
   *  Returns the feedbackId so the caller can match the response. */
  public requestPohDowntimes(
    id: string,
    start: number,
    end: number,
    period: number,
    offset: number,
    stateFilters: any[],
  ): number {
    const uid = UID();
    this.ws.sendWsRequest(
      { name: APP_CONSTANTS.REQUEST_MESSAGES.GET_ACCUMULATED_DOWNTIMES, feedbackId: uid },
      {
        filter: { timePoint: { $gte: start, $lt: end }, itemId: { $eq: id } },
        span: { timePoint: { period, offset } },
        aggMask: {
          timePoint: { max: 1, min: 1 },
          data: { timePoint: { max: 1, min: 1 }, duration: { sum: 1 }, state: { count: 1 } },
        },
        stateFilters,
      },
    );
    return uid;
  }

  /** Sends FindMotorState for the given well and time range. */
  public requestFindMotorState(id: string, start: number, end: number): void {
    this.ws.sendWsRequest(
      { name: APP_CONSTANTS.REQUEST_MESSAGES.GET_MOTOR_STATE },
      {
        filter: {
          itemId: { $eq: id },
          timePoint: { $gte: start, $lte: end },
          data: { state: { $ne: 0 }, isReaffirmation: { $ne: 1 } },
        },
        options: { sort: { timePoint: 1 } },
      },
    );
  }

  /** Sends a mediation command to start the well: ResetHoldRequest + RecoverViolations,
   *  unconditionally, regardless of the well's current malfunction state.
   *  No-op when the well is already in a stopping transition. */
  public startWell(): void {
    if (!this.isWellStateStopping()) {
      const id = this._signals.currentWell()?.id;
      const mediations = [MediationType.ResetHoldRequest, MediationType.RecoverViolations];
      this.ws.sendWsRequest(
        { name: APP_CONSTANTS.REQUEST_MESSAGES.SET_MEDIATE_WELL },
        { itemId: id, mediations },
      );
      this.watchMediateWell().subscribe();
    }
  }

  /** Sends a SetHoldRequest mediation command to stop the currently selected well. */
  public stopWell(): void {
    const id = this._signals.currentWell()?.id;
    this.ws.sendWsRequest(
      { name: APP_CONSTANTS.REQUEST_MESSAGES.SET_MEDIATE_WELL },
      { itemId: id, mediations: [MediationType.SetHoldRequest] },
    );
    this.watchMediateWell().subscribe();
  }

  /** Sends a Reboot mediation command to reboot the currently selected well's controller. */
  public rebootWell(id: string): void {
    this.toastr.info('Reboot command sent to the controller...');
    this.ws.sendWsRequest(
      { name: APP_CONSTANTS.REQUEST_MESSAGES.SET_MEDIATE_WELL },
      { itemId: id, mediations: [MediationType.Reboot] },
    );
  }

  /** Sends a DeleteWell request to permanently remove the well with the given id. */
  public deleteWell(id: string): void {
    this.ws.sendWsRequest(
      { name: APP_CONSTANTS.REQUEST_MESSAGES.DELETE_WELL },
      {
        filter: {
          id: {
            $eq: id,
          },
        },
      },
    );
  }

  /** Sends the full malfunction-reset mediation array to clear all active malfunctions and restart the well. */
  public resetMalfunctions(): void {
    if (!this.isWellStateStopping()) {
      const id = this._signals.currentWell()?.id;
      const mediations = resetMalfunctionArray;
      this.toastr.info('Resetting Malfunctions...');
      this.ws.sendWsRequest(
        { name: APP_CONSTANTS.REQUEST_MESSAGES.SET_MEDIATE_WELL },
        { itemId: id, mediations },
      );
    }
  }

  /** Sends the full violation-reset mediation array to clear all active violations and restart the well. */
  public resetViolations(): void {
    if (!this.isWellStateStopping()) {
      const id = this._signals.currentWell()?.id;
      const mediations = resetViolationArray;
      this.toastr.info('Resetting Violations...');
      this.ws.sendWsRequest(
        { name: APP_CONSTANTS.REQUEST_MESSAGES.SET_MEDIATE_WELL },
        { itemId: id, mediations },
      );
    }
  }

  /** Sends a Calibrate mediation command to trigger jack calibration on the currently selected well. */
  public calibrateJack(): void {
    const id = this._signals.currentWell()?.id;
    const mediations = [MediationType.Calibrate];
    this.toastr.info('Calibrating...');
    this.ws.sendWsRequest(
      { name: APP_CONSTANTS.REQUEST_MESSAGES.SET_MEDIATE_WELL },
      { itemId: id, mediations },
    );
  }

  /** Returns true when the well is in the BEGIN_Starting transitional state. */
  public isWellStateStarting(): boolean {
    return (this._signals.operationalState()?.state ?? 0) === OperationalState.BEGIN_Starting;
  }

  /** Returns true when the well's operational state falls within the stopping range
   *  (BEGIN_Stopping through END_Stopping inclusive). */
  public isWellStateStopping(): boolean {
    const state = this._signals.operationalState()?.state ?? 0;
    return state >= OperationalState.BEGIN_Stopping && state <= OperationalState.END_Stopping;
  }

  /** Returns true when the well is actively pumping (Running state). */
  public isWellStateRunning(): boolean {
    return (this._signals.operationalState()?.state ?? 0) === OperationalState.Running;
  }

  /** Returns true when the well has fully stopped (BEGIN_Stopped state). */
  public isWellStateStopped(): boolean {
    return (this._signals.operationalState()?.state ?? 0) === OperationalState.BEGIN_Stopped;
  }

  // #endregion

  // #region Private Helpers

  /** Enriches a raw stroke event with derived display values:
   *  - spm: strokes per minute (60 / duration)
   *  - fillage: converted from fraction to percentage
   *  - oilInst / totalInst: instantaneous bbl/day (produced / duration * 8.91 conversion)
   *  - gasInst: instantaneous Mscf/day (produced / duration * 86.4 conversion)
   *  - waterInst: derived as totalInst − oilInst */
  public processStroke(event: any) {
    const oilInst = +((event.oilProduced / event.duration) * 8.91).toFixed(3);
    const totalInst = +((event.liquidProduced / event.duration) * 8.91).toFixed(3);
    return {
      ...event,
      spm: +(60 / event.duration).toFixed(2),
      fillage: +(event.fillage * 100).toFixed(3),
      oilInst,
      totalInst,
      gasInst: +((event.gasProduced / event.duration) * 86.4).toFixed(3),
      waterInst: +(totalInst - oilInst).toFixed(3),
      downstrokeMaxVelocity:
        event.downstrokeMaxVelocity != null ? +event.downstrokeMaxVelocity.toFixed(2) : null,
      sfMaxLoad: +event.sfMaxLoad * 1000,
      gearboxLoading: +event.gearboxLoading,
    };
  }

  /** Normalizes raw dynagraph card data for chart rendering:
   *  - Shifts surface positions so the minimum is at 0
   *  - Shifts downhole positions relative to dhMinPosn, deduplicating zero-length segments
   *  - Attaches derived min/max extent fields used by the chart component */
  private processStrokeCard(event: any) {
    // A card can arrive with only one curve populated — guard each block so a missing curve
    // is left as-is instead of throwing (which would abort this whole handler and drop the
    // card, since the caller sets the signal from this method's return value).
    const sfCurve: any[] = event.sfCurve ?? [];
    const dhCurve: any[] = event.dhCurve ?? [];

    if (sfCurve.length) {
      let sfCurveMinPos = sfCurve[0].posn;
      let sfCurveMaxPos = sfCurve[0].posn;
      let sfCurveMinLoad = sfCurve[0].load;
      let sfCurveMaxLoad = sfCurve[0].load;

      // First pass: find true min/max before any normalization.
      // The original single-pass approach updated sfCurveMinPos mid-loop and subtracted
      // the running minimum from each point, which caused downstroke points that dip below
      // the upstroke origin to be collapsed to 0 while earlier points were under-normalized.
      for (const point of sfCurve) {
        if (point.posn < sfCurveMinPos) sfCurveMinPos = point.posn;
        if (point.posn > sfCurveMaxPos) sfCurveMaxPos = point.posn;
        if (point.load < sfCurveMinLoad) sfCurveMinLoad = point.load;
        if (point.load > sfCurveMaxLoad) sfCurveMaxLoad = point.load;
      }

      // Second pass: normalize positions using the true minimum.
      sfCurve.forEach((point: { posn: number; load: number }) => {
        point.posn -= sfCurveMinPos;
      });

      event.sfMaxPosn = sfCurveMaxPos - sfCurveMinPos;
      event.sfMinPosn = 0;
      event.sfMinLoad = sfCurveMinLoad * 1000;
    }

    if (dhCurve.length) {
      const dhMinPosn = event.dhMinPosn;
      dhCurve.forEach((point: { load: number; posn: number }) => {
        if (point.posn - dhMinPosn === 0 && dhCurve.length > 1) {
          point.posn = dhCurve[dhCurve.indexOf(point) - 1]?.posn ?? point.posn;
        } else {
          point.posn -= dhMinPosn;
        }
      });

      event.dhMinPosn = 0;
      event.dhMaxPosn -= dhMinPosn;
    }

    event.sfCurve = sfCurve;
    event.dhCurve = dhCurve;
    return event;
  }

  /** Transforms the raw violationOrigins array into a keyed map and converts absolute
   *  timestamps to milliseconds-elapsed-since-now for display as relative durations.
   *  Also merges the `count` field from the `violations` array into each entry. */
  private processViolationsData(body: any): Record<string, any> {
    const result: Record<string, any> = {};
    const counts = body?.data?.violationOrigins ?? [];
    const now = +new Date();
    for (let i = 0; i < counts.length; i++) {
      result[counts[i][0]] = counts[i][1];
      result[counts[i][0]].first = counts[i][1].first ? now - counts[i][1].first : 0;
    }
    const violations: [string, any][] = body?.data?.violations ?? [];
    for (const [key, val] of violations) {
      if (result[key]) {
        result[key].count = val.count;
      }
    }
    return result;
  }

  /** Processes the latest sensor data sample, extending it with:
   *  - tubingPressure / casingPressure: extracted from the analog array by channel name
   *  - positionPressure: inclinometer angle converted to rod position using jack geometry
   *    (angle_rad × dimA − posnOffset)
   *  - loadPressure: raw loadcell analog value
   *  - sfTubingPressure / sfCasingPressure: default pressure constants from the well config */
  private processSensorData(data: SensorsData[]) {
    const event = data.slice(-1)[0];
    if (!event || !Array.isArray(event.analog)) return event;
    const well = this._signals.currentWell();
    const dimA = well?.jack?.dimA;
    const posnOffset = well?.jack?.posnOffset;
    const sfTubingPressure = well?.tubing?.sfTubingFluidInnerPressureDefault;
    const sfCasingPressure = well?.casing?.sfCasingGasInnerPressureDefault;
    const extendedEvent: any = { ...event, sfTubingPressure, sfCasingPressure };
    for (const analogItem of event.analog) {
      if (
        Array.isArray(analogItem) &&
        typeof analogItem[0] === 'string' &&
        analogItem[1] &&
        typeof analogItem[1].value === 'number'
      ) {
        const analogType = analogItem[0].split(' - ')[0];
        if (analogType === 'Tubing Pressure') extendedEvent.tubingPressure = analogItem[1].value;
        if (analogType === 'Casing Pressure') extendedEvent.casingPressure = analogItem[1].value;
        if (analogType === 'Inclinometer' && dimA && posnOffset)
          extendedEvent.positionPressure =
            analogItem[1].value * (Math.PI / 180) * dimA - posnOffset;
        if (analogType === 'Loadcell') extendedEvent.loadPressure = analogItem[1].value;
      }
    }
    return extendedEvent;
  }

  /** Maps the GET_ACCUMULATED_RUNTIME_CYCLES response array to a { runTime, cycles } object
   *  indexed by position (item[0] → runTime, item[1] → cycles). */
  private processRuntimeCycles(items: SpanAggregateWellMotorState_RES[]) {
    const data: any = {};
    const keys = ['runTime', 'cycles'];
    items.forEach((item, i) => {
      data[keys[i]] = item.records?.[0]?.data ?? null;
    });
    return data;
  }

  /** Updates the connectionStatus of a well in the wells signal by id.
   *  If the well is not yet in the list (status arrived before the well list), a minimal
   *  Well object is added so the map markers reflect the correct status immediately.
   *  Passed through dedupeWellsById as a safety net in case the full well list replaces
   *  this placeholder while a status push for the same id is still in flight. */
  private updateConnectionStatusInWellList(wellId: string, connectionStatus: number) {
    const currentWells = this._signals.wells();
    const connectionStatusName =
      Object.values(APP_CONSTANTS.CONNECTION_STATUSES).find((s) => s.id === connectionStatus)
        ?.name ?? APP_CONSTANTS.CONNECTION_STATUSES.UNKNOWN.name;
    const existingWell = currentWells?.find((w) => w.id === wellId);
    if (!existingWell) {
      const newWell: Well = {
        id: wellId,
        connectionStatus: { id: connectionStatus, name: connectionStatusName },
      };
      this._signals.wells.set(this.dedupeWellsById([...(currentWells ?? []), newWell]));
      return;
    }
    const updatedWells = currentWells?.map((well) =>
      well.id === wellId
        ? {
            ...well,
            connectionStatus: { id: connectionStatus, name: connectionStatusName },
          }
        : well,
    );
    if (updatedWells) {
      this._signals.wells.set(this.dedupeWellsById(updatedWells));
    }
  }

  /** Ensures the shared wells list never contains two entries for the same id. The list is a
   *  single source of truth read by both the map and list views, so any accidental duplicate
   *  (e.g. a connection-status placeholder racing the full well-list load) must never surface —
   *  keeps the last (most complete) entry per id while preserving first-seen ordering. Wells
   *  without an id are passed through as-is since they can't collide by id. */
  protected dedupeWellsById(wells: Well[]): Well[] {
    const byId = new Map<string, Well>();
    const withoutId: Well[] = [];
    for (const well of wells) {
      if (well.id) byId.set(well.id, well);
      else withoutId.push(well);
    }
    return [...byId.values(), ...withoutId];
  }

  // #endregion
}
